import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ComponentLike } from '@glint/template';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task, type TaskForAsyncTaskFunction } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { ConditionalStrategy, RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { safeRollback } from 'lumen/utils/pricing-assistant/recommendation-settings';
import { timeout } from 'lumen/utils/waiters';

import styles from './strategy-card.module.css';

interface StrategyComponentSignature {
  Args: {
    componentSeasonEndDt: Dayjs | null;
    componentSeasonStartDt: Dayjs | null;
    isEditMode: boolean;
    isSaveStrategyTaskRunning: boolean;
    roomOffsetHierarchy?: string[];
    saveAttempted: boolean;
    strategy: RecommendationStrategy;
  };
}

interface SettingsRecommendationsStrategyCardSignature {
  Args: {
    defaultStrategy?: RecommendationStrategy;
    initiallyOpened?: boolean;
    isSomeStrategyCardBeingEdited?: boolean;
    pmsLeadingRoomName?: string;
    roomOffsetHierarchy?: string[];
    saveStrategyTask: TaskForAsyncTaskFunction<
      { drop: boolean },
      (
        strategyRecord: RecommendationStrategy,
      ) => Promise<{ isSuccess: boolean } | null>
    >;
    seasonalStrategies: RecommendationStrategy[];
    setSomeStrategyCardBeingEditedState?: (isEditMode: boolean) => void;
    strategy: RecommendationStrategy;
    strategyComponent: ComponentLike<StrategyComponentSignature>;
  };
  Element: HTMLDetailsElement;
}

export default class SettingsRecommendationsStrategyCard extends Component<SettingsRecommendationsStrategyCardSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked componentSeasonEndDt: Dayjs | null = null;
  // Keeping these locally in the component to store the values for the datepickers before save is clicked
  // if we update the strategy object directly, we might impact sorting in parent components
  @tracked componentSeasonStartDt: Dayjs | null = null;
  @tracked isEditMode =
    this.isStrategyNew ||
    this.args.strategy.isCompsetPositioningStartingState ||
    false;
  @tracked isOpen = this.args.initiallyOpened || this.isStrategyNew || false;
  // We only want to show the error message if the user has changed the date range
  @tracked isStrategyDateRangeChanged = false;
  @tracked saveAttempted = false;

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  debouncedUpdateStrategyTitleTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      await timeout();

      this.updateStrategyTitle(event);
    },
  );

  get dateRangeTitle(): string {
    const { componentSeasonEndDt, componentSeasonStartDt } = this;

    if (!componentSeasonStartDt || !componentSeasonEndDt) {
      return '';
    }

    if (componentSeasonStartDt.isSame(componentSeasonEndDt, 'day')) {
      return componentSeasonStartDt.format('DD MMM YYYY');
    }

    return `${componentSeasonStartDt.format(
      'DD MMM YYYY',
    )} - ${componentSeasonEndDt.format('DD MMM YYYY')}`;
  }

  // On conditional strategy deletion, the conditional strategy is unlinked from the strategy,
  // so this.strategy.conditionalStrategies won't have the deleted conditional strategies.
  // Therefore, we need to get the deleted conditional strategies from the store
  get deletedConditionalStrategies(): ConditionalStrategy[] {
    const strategyStartDate = this.strategy.seasonStartDt;
    const strategyEndDate = this.strategy.seasonEndDt;

    const conditionalStrategies = [
      ...this.store.peekAll<ConditionalStrategy>('conditional-strategy'),
    ];

    return conditionalStrategies.filter((conditionalStrategy) => {
      const { conditions, seasonEndDt, seasonStartDt } = conditionalStrategy;

      // Skip if conditions are present (not deleted)
      if (conditions !== null) {
        return false;
      }

      // Default conditional strategy and season strategy date range is null
      if (seasonStartDt === null && seasonEndDt === null) {
        return true;
      }

      // Seasonal conditional strategy date range is same as the strategy date range
      if (
        seasonStartDt &&
        seasonEndDt &&
        strategyStartDate &&
        strategyEndDate
      ) {
        return (
          seasonStartDt.isSame(strategyStartDate, 'day') &&
          seasonEndDt.isSame(strategyEndDate, 'day')
        );
      }

      return false;
    });
  }

  get formattedStrategyTitle(): string {
    if (!this.isStrategyDefault) {
      return this.strategy.name;
    }

    let defaultStrategyTitle = this.intl.t(
      'pricing_assistant.recommendations.default_strategy',
    );

    if (
      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated &&
      // We don't show a leading room in ORP because the settings are applied to all the rooms
      !this.access.hasPricingAssistantOpenPricingFeature
    ) {
      defaultStrategyTitle += ` (${this.args.pmsLeadingRoomName})`;
    }

    return defaultStrategyTitle;
  }

  get hasChanges(): boolean {
    const {
      deletedConditionalStrategies,
      isStrategyDateRangeChanged,
      strategy,
    } = this;

    const hasConditionalStrategyChanges = [
      ...strategy.conditionalStrategies,
    ].some((conditionalStrategy) => {
      return (
        conditionalStrategy.isNew || conditionalStrategy.hasDirtyAttributes
      );
    });

    return (
      strategy.hasDirtyAttributes ||
      hasConditionalStrategyChanges ||
      deletedConditionalStrategies.length > 0 ||
      isStrategyDateRangeChanged
    );
  }

  get hasValidationErrors(): boolean {
    return (
      this.isStrategyNameValid.hasError ||
      this.strategy.isStrategyInvalid ||
      this.strategyExactOverlappingDaysError.hasError ||
      (this.strategy.isSeasonalStrategy &&
        (this.componentSeasonStartDt === null ||
          this.componentSeasonEndDt === null))
    );
  }

  get hasValidationMessages(): boolean {
    if (!this.saveAttempted) {
      return false;
    }

    return (
      this.strategyExactOverlappingDaysError.hasError ||
      (this.strategy.isStrategyInvalid &&
        !!this.strategy.errorMessageForInvalidStrategy) ||
      (this.strategyOverlappingDaysWarning.hasWarning &&
        this.isStrategyDateRangeChanged) ||
      this.isStrategyNameValid.hasError
    );
  }

  get isCompsetPositioningStartingStateWithNoAssignments(): boolean {
    const { strategy } = this;
    const unassigned =
      strategy.compsetPositioningCompetitorsLeftToAssignInStartingState;

    if (
      unassigned.length === 0 ||
      strategy.settings.settings_type !== 'compset_positioning'
    ) {
      return false;
    }

    const totalCompetitors = Object.keys(
      strategy.settings.day_of_week[1] ?? {},
    ).length;

    return unassigned.length === totalCompetitors;
  }

  get isDatepickerFormFieldTouched(): boolean {
    // If the strategy is new, we don't want isTouched to be true.
    // Otherwise users will immediately get to see error states, which isn't nice UX
    // Reason: we initialize new strategies without date ranges, which are required
    return !this.strategy.isNew || this.saveAttempted;
  }

  // Check if the current strategy is overlapped by another strategy
  get isOverlappedStrategy(): boolean {
    const strategyCreatedAt = this.strategy.createdAt;

    // An overlapping strategy exists whose created time is after the current strategy created time
    return this.strategyOverlappingDaysWarning.overlappingStrategies.some(
      ({ createdAt }) => {
        return dayjs(createdAt).isAfter(strategyCreatedAt);
      },
    );
  }

  // Reset to default button is disabled when the seasonal strategy settings are same as the default strategy settings and no conditional strategies are present.
  get isResetToDefaultButtonDisabled(): boolean {
    if (this.args.strategy.hasMany('conditionalStrategies').ids().length) {
      return false;
    }

    return (
      JSON.stringify(this.strategy.settings) ===
      JSON.stringify(this.args.defaultStrategy?.settings)
    );
  }

  get isStrategyDefault(): boolean {
    return this.args.strategy.isDefaultStrategy;
  }

  get isStrategyNameValid(): { hasError: boolean; message: string } {
    if (this.strategy && this.strategy.name?.trim?.()?.length === 0) {
      return {
        hasError: true,
        message: this.intl.t(
          'pricing_assistant.recommendations.seasonal_strategy.name_required_error',
        ),
      };
    }

    const isNameUsed = this.args.seasonalStrategies.some((strategy) => {
      return (
        strategy.id !== this.strategy.id && strategy.name === this.strategy.name
      );
    });

    const message = isNameUsed
      ? this.intl.t(
          'pricing_assistant.recommendations.seasonal_strategy.name_used_error',
        )
      : '';

    return {
      hasError: Boolean(message),
      message,
    };
  }

  get isStrategyNew(): boolean {
    // isNew is an ember-data property that is set to true when a record is created but not yet saved
    // https://api.emberjs.com/ember-data/4.12/classes/Model/properties/isNew?anchor=isNew
    return this.args.strategy.isNew;
  }

  get isStrategySeasonal(): boolean {
    return this.args.strategy.isSeasonalStrategy;
  }

  get maxAllowedDatepickerEndDate(): Dayjs {
    return this.hotelAndMarketService
      .getLocalTime()
      .endOf('day')
      .add(3, 'years');
  }

  get minAllowedDatepickerEndDate(): Dayjs {
    if (this.componentSeasonStartDt) {
      return this.componentSeasonStartDt;
    }

    return this.minAllowedDatepickerStartDate;
  }

  get minAllowedDatepickerStartDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime().startOf('day');
  }

  // Strategies that overlap with the current strategy
  get overlappingStrategies(): {
    exactOverlappingStrategies: RecommendationStrategy[];
    overlappingStrategies: RecommendationStrategy[];
  } {
    const strategyId = this.args.strategy.id;
    const startDate = this.componentSeasonStartDt;
    const endDate = this.componentSeasonEndDt;

    const exactOverlappingStrategies: RecommendationStrategy[] = [];
    const overlappingStrategies: RecommendationStrategy[] = [];

    this.args.seasonalStrategies.forEach((strategy) => {
      const { id, seasonEndDt, seasonStartDt } = strategy;

      if (id === strategyId) {
        return;
      }

      if (!startDate || !endDate || !seasonStartDt || !seasonEndDt) {
        return;
      }

      const isExactOverlap =
        seasonStartDt.isSame(startDate, 'day') &&
        seasonEndDt.isSame(endDate, 'day');

      if (isExactOverlap) {
        exactOverlappingStrategies.push(strategy);
        return;
      }

      const isOverlap =
        startDate.isBetween(seasonStartDt, seasonEndDt, 'day', '[]') ||
        endDate.isBetween(seasonStartDt, seasonEndDt, 'day', '[]') ||
        seasonStartDt.isBetween(startDate, endDate, 'day', '[]') ||
        seasonEndDt.isBetween(startDate, endDate, 'day', '[]');

      if (isOverlap) {
        overlappingStrategies.push(strategy);
        return;
      }
    });

    return {
      exactOverlappingStrategies,
      overlappingStrategies,
    };
  }

  get secondaryButtonText(): string {
    if (this.isStrategyNew) {
      return this.intl.t('general.delete');
    }

    return this.intl.t('general.cancel');
  }

  get showDeleteButton(): boolean {
    if (!this.isStrategySeasonal) {
      return false;
    }

    return !this.access
      .isPricingAssistantRecommendationSettingsActionOrInputDisabled;
  }

  get strategyExactOverlappingDaysError(): {
    exactOverlappingStrategy?: RecommendationStrategy;
    hasError: boolean;
    message: string;
  } {
    // There can be only one exact overlapping strategy
    const exactOverlappingStrategy =
      this.overlappingStrategies.exactOverlappingStrategies[0];

    const message = exactOverlappingStrategy
      ? this.intl.t(
          'pricing_assistant.recommendations.seasonal_strategy.exact_overlap_error',
          {
            strategyName: exactOverlappingStrategy.name,
          },
        )
      : '';

    return {
      exactOverlappingStrategy,
      hasError: Boolean(message),
      message,
    };
  }

  get strategyOverlappingDaysWarning(): {
    hasWarning: boolean;
    overlappingStrategies: RecommendationStrategy[];
  } {
    const strategyCreatedAt = this.strategy.createdAt;
    const { overlappingStrategies } = this.overlappingStrategies;

    const hasWarning = overlappingStrategies.some(({ createdAt }) => {
      return dayjs(createdAt).isBefore(strategyCreatedAt);
    });

    return {
      hasWarning,
      overlappingStrategies,
    };
  }

  constructor(
    owner: Owner,
    args: SettingsRecommendationsStrategyCardSignature['Args'],
  ) {
    super(owner, args);

    this.componentSeasonStartDt = this.strategy.seasonStartDt;
    this.componentSeasonEndDt = this.strategy.seasonEndDt;
  }

  // Edge case: If user makes a change and then navigates away to another page (main recommendations route) without saving/canceling and then comes back
  // Rollback the unsaved changes on destroy
  willDestroy(): void {
    super.willDestroy();

    safeRollback(this.args.strategy);
  }

  @action
  disableEditMode(): void {
    this.isEditMode = false;
    this.args.setSomeStrategyCardBeingEditedState?.(this.isEditMode);
  }

  @action
  enableEditMode(): void {
    this.isOpen = true;
    this.isEditMode = true;

    this.args.setSomeStrategyCardBeingEditedState?.(this.isEditMode);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/require-await
  async onCancelClick(): Promise<void> {
    // Case 1: If it's a new seasonal strategy, delete it
    if (this.isStrategyNew) {
      this.args.strategy.unloadRecord();
      // Case 2: If it's an existing strategy, rollback the changes
    } else {
      if (this.args.strategy.hasMany('conditionalStrategies').ids().length) {
        const indices: number[] = [];

        this.args.strategy.conditionalStrategies.forEach(
          (conditionalStrategy, index) => {
            if (conditionalStrategy.isNew) {
              // Case 2.1: If strategy has new conditional strategies, delete them
              conditionalStrategy.unloadRecord();
              indices.push(index);
            } else {
              // Case 2.2: If strategy already has conditional strategies, rollback their changes
              conditionalStrategy.rollbackAttributes();
            }
          },
        );

        indices.reverse().forEach((index) => {
          // Case 2.1 continued
          this.args.strategy.conditionalStrategies.splice(index, 1);
        });
      } else {
        // Case 2.3: If conditional strategy has been deleted, retrieve it from the store, rollback its changes and add it back to the strategy
        // We use the conditional strategies fetched from the store to take into account the conditional strategies that have been deleted
        this.deletedConditionalStrategies.forEach((conditionalStrategy) => {
          conditionalStrategy.rollbackAttributes();

          const ids = this.args.strategy.hasMany('conditionalStrategies').ids();

          if (!ids.includes(conditionalStrategy.id)) {
            this.args.strategy.conditionalStrategies.push(conditionalStrategy);
          }
        });
      }

      this.args.strategy.rollbackAttributes();

      // Reset the date range to the actual recommendation strategy record values
      if (this.isStrategyDateRangeChanged) {
        this.componentSeasonStartDt = this.strategy.seasonStartDt;
        this.componentSeasonEndDt = this.strategy.seasonEndDt;
      }
    }

    this.disableEditMode();

    // Close the card after cancel if it's a seasonal strategy
    if (this.isStrategySeasonal) {
      this.isOpen = false;
    }

    this.isStrategyDateRangeChanged = false;
    this.saveAttempted = false;
  }

  @action
  async onDeleteClick(): Promise<void> {
    const isDeleteConfirmed = await this.alerting.showWarningAlert({
      title: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy',
      ),
      text: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy_text',
        {
          strategy: this.strategy.name,
        },
      ),
      dangerMode: true,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.delete'),
        },
      },
    });

    if (isDeleteConfirmed) {
      await this.args.strategy.destroyRecord();
      this.disableEditMode();
    }
  }

  @action
  // eslint-disable-next-line @typescript-eslint/require-await
  async onResetToDefaultClick(): Promise<void> {
    const { defaultStrategy } = this.args;

    if (!defaultStrategy) {
      return;
    }

    this.strategy.settings = defaultStrategy.settings;
    this.strategy.conditions = defaultStrategy.conditions;

    // Remove all conditional strategies when resetting a strategy
    this.strategy.conditionalStrategies.forEach((conditionalStrategy) => {
      conditionalStrategy.conditions = null;
      conditionalStrategy.unloadRecord();
    });

    // @ts-expect-error: Incorrect type
    this.strategy.conditionalStrategies = [] as ConditionalStrategy[];
  }

  @action
  async onSaveClick(): Promise<void> {
    if (this.args.saveStrategyTask.isRunning) {
      return;
    }

    if (!this.hasChanges) {
      await this.onCancelClick();
      return;
    }

    /* Entering the starting state sets all competitors to an init value, which dirties the record
       and passes the hasChanges gate above — but no real assignment was made, so treat as cancel */
    if (this.isCompsetPositioningStartingStateWithNoAssignments) {
      await this.onCancelClick();
      return;
    }

    this.saveAttempted = true;

    if (this.hasValidationErrors) {
      return;
    }

    const { settingsType } = this.strategy;

    if (this.isStrategyDateRangeChanged) {
      this.strategy.seasonStartDt = this.componentSeasonStartDt;
      this.strategy.seasonEndDt = this.componentSeasonEndDt;
    }

    /* If conditional strategies have invalid price range conditions,
       remove the incomplete conditions and conditional strategies before saving */
    if (settingsType === 'price_range') {
      const indices: number[] = [];

      this.strategy.conditionalStrategies.forEach(
        (conditionalStrategy, index) => {
          if (!conditionalStrategy.hasInvalidPriceRangeCondition) {
            return;
          }

          conditionalStrategy.unloadRecord();
          indices.push(index);
        },
      );

      indices.reverse().forEach((index) => {
        this.strategy.conditionalStrategies.splice(index, 1);
      });
    }

    // Clear out the deleted conditional strategies
    this.deletedConditionalStrategies.forEach((conditionalStrategy) => {
      conditionalStrategy.unloadRecord();
    });

    const result = await this.args.saveStrategyTask.perform(this.strategy);

    if (!result?.isSuccess) {
      return;
    }

    this.disableEditMode();

    // Close the card after save if it's a seasonal strategy
    if (this.isStrategySeasonal) {
      this.isOpen = false;
    }

    this.isStrategyDateRangeChanged = false;
    this.saveAttempted = false;

    // In case a new condition is added: Ember data doesn't replace the dummy conditional strategy (with null ID) with the actual conditional strategy returned from the BE
    // Instead, it keeps the dummy conditional strategy and adds the actual conditional strategy as a new record both referencing the same strategy
    // Therefore, we need to manually remove the dummy conditional strategy after save
    const indices: number[] = [];

    this.strategy.conditionalStrategies.forEach(
      (conditionalStrategy, index) => {
        if (conditionalStrategy.id !== null) {
          return;
        }

        conditionalStrategy.unloadRecord();
        indices.push(index);
      },
    );

    indices.reverse().forEach((index) => {
      this.strategy.conditionalStrategies.splice(index, 1);
    });

    if (settingsType === 'compset_positioning') {
      const eventName = this.isStrategyDefault
        ? 'Default Competitor impact saved'
        : 'Seasonal Competitor impact edited';

      this.usageAnalytics.track(eventName, {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    } else if (settingsType === 'price_range') {
      const eventName = this.isStrategyDefault
        ? 'Default Min/Max prices saved'
        : 'Seasonal Min/Max prices saved';

      this.usageAnalytics.track(eventName, {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    }
  }

  @action
  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  updateEndDate(value: Dayjs | null): void {
    if (value) {
      this.componentSeasonEndDt = this.hotelAndMarketService.toLocalTime(
        value.format('YYYY-MM-DD'),
      );
    } else {
      this.componentSeasonEndDt = null;
    }

    this.isStrategyDateRangeChanged = true;
  }

  @action
  updateStartDate(value: Dayjs | null): void {
    if (value) {
      this.componentSeasonStartDt = this.hotelAndMarketService.toLocalTime(
        value.format('YYYY-MM-DD'),
      );

      if (this.componentSeasonStartDt.isAfter(this.componentSeasonEndDt)) {
        this.componentSeasonEndDt = null;
      }
    } else {
      this.componentSeasonStartDt = null;
    }

    this.isStrategyDateRangeChanged = true;
  }

  @action
  updateStrategyTitle(event: Event): void {
    this.strategy.name = (event.target as HTMLInputElement).value;
  }

  <template>
    <div class={{styles.card}}>
      {{#if this.isEditMode}}
        <div
          class="{{styles.card-header-wrapper}}
            gap-800
            {{if this.isStrategyDefault 'items-center'}}"
        >
          <div class="flex w-full flex-col justify-between">
            <div class="flex flex-col gap-200">
              <div class="flex flex-row justify-between">
                {{#if this.isStrategySeasonal}}
                  <div class="flex w-full flex-row gap-200">
                    <div class="flex flex-row">
                      <div>
                        {{! Note: wrapping div mainly needed to avoid incorrect DatepickerFormField rendering (older component). Don't remove without double-checking! }}
                        <GeneralFormsFormFieldsDatepickerFormField
                          {{! @glint-expect-error: Incorrect type }}
                          @datePickerMaxDate={{this.maxAllowedDatepickerEndDate}}
                          @datePickerMinDate={{this.minAllowedDatepickerStartDate}}
                          @isInErrorState={{and
                            this.strategyExactOverlappingDaysError.hasError
                            this.isStrategyDateRangeChanged
                          }}
                          @isInWarningState={{and
                            this.strategyOverlappingDaysWarning.hasWarning
                            this.isStrategyDateRangeChanged
                          }}
                          @isTouched={{this.isDatepickerFormFieldTouched}}
                          @label={{t "events.start_date"}}
                          @onSelectDate={{this.updateStartDate}}
                          @removePadding={{true}}
                          @requiredErrorMessage={{t
                            "pricing_assistant.recommendations.seasonal_strategy.start_date_required_error"
                          }}
                          @selectedDate={{this.componentSeasonStartDt}}
                          {{! @glint-expect-error: Incorrect type }}
                          class="date-picker__form-field--small mr-200"
                        />
                      </div>
                      <div>
                        {{! Note: wrapping div mainly needed to avoid incorrect DatepickerFormField rendering (older component). Don't remove without double-checking! }}
                        <GeneralFormsFormFieldsDatepickerFormField
                          {{! @glint-expect-error: Incorrect type }}
                          @datePickerMaxDate={{this.maxAllowedDatepickerEndDate}}
                          @datePickerMinDate={{this.minAllowedDatepickerEndDate}}
                          @isInErrorState={{and
                            this.strategyExactOverlappingDaysError.hasError
                            this.isStrategyDateRangeChanged
                          }}
                          @isInWarningState={{and
                            this.strategyOverlappingDaysWarning.hasWarning
                            this.isStrategyDateRangeChanged
                          }}
                          @isTouched={{this.isDatepickerFormFieldTouched}}
                          @label={{t "events.end_date"}}
                          @onSelectDate={{this.updateEndDate}}
                          @removePadding={{true}}
                          @requiredErrorMessage={{t
                            "pricing_assistant.recommendations.seasonal_strategy.end_date_required_error"
                          }}
                          @selectedDate={{this.componentSeasonEndDt}}
                          {{! @glint-expect-error: Incorrect type }}
                          class="date-picker__form-field--small"
                        />
                      </div>
                    </div>

                    <div
                      class="{{styles.input-title-wrapper}}
                        mt-500 flex w-full flex-row"
                    >
                      {{! mt-500 added to compensate the DatepickerFormField label height }}
                      <PrismInput
                        @hasErrors={{this.isStrategyNameValid.hasError}}
                        @onInput={{this.debouncedUpdateStrategyTitleTask.perform}}
                        @removePadding={{true}}
                        @value={{this.strategy.name}}
                        data-test-field="Name"
                      />
                    </div>
                  </div>
                {{else}}
                  <div class="flex flex-row">
                    <h3
                      data-test-strategy-name
                    >{{this.formattedStrategyTitle}}</h3>
                  </div>
                {{/if}}
              </div>
              {{#if this.hasValidationMessages}}
                <div
                  class="text-200--medium text-critical-default flex flex-col gap-100"
                >
                  {{#if this.strategyExactOverlappingDaysError.hasError}}
                    <div class="flex flex-row gap-100">
                      <PrismIcon
                        @color="critical"
                        @name="circle-error-filled"
                        @size="small"
                      />
                      {{this.strategyExactOverlappingDaysError.message}}
                    </div>
                  {{/if}}

                  {{#if
                    (and
                      this.strategy.isStrategyInvalid
                      this.strategy.errorMessageForInvalidStrategy
                    )
                  }}
                    <div class="flex flex-row gap-100">
                      <PrismIcon
                        @color="critical"
                        @name="circle-error-filled"
                        @size="small"
                      />
                      {{this.strategy.errorMessageForInvalidStrategy}}
                    </div>
                  {{/if}}

                  {{#if
                    (and
                      this.strategyOverlappingDaysWarning.hasWarning
                      this.isStrategyDateRangeChanged
                    )
                  }}
                    <div class="text-warning-default flex flex-row gap-100">
                      <PrismIcon
                        @color="warning"
                        @name="circle-error-filled"
                        @size="small"
                      />
                      {{t
                        "pricing_assistant.recommendations.seasonal_strategy.days_overlap.warning"
                      }}
                    </div>
                  {{/if}}

                  {{#if this.isStrategyNameValid.hasError}}
                    <div class="flex flex-row gap-100">
                      <PrismIcon
                        @color="critical"
                        @name="circle-error-filled"
                        @size="small"
                      />{{this.isStrategyNameValid.message}}
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          </div>
          <div
            class="flex flex-row gap-200
              {{if this.isStrategySeasonal 'mt-500'}}"
          >
            {{! mt-500 added to compensate the DatepickerFormField label height }}
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{@saveStrategyTask.isRunning}}
              @label={{this.secondaryButtonText}}
              @onClick={{this.onCancelClick}}
              data-test-button="Cancel"
            />
            <PrismButton
              @isLoading={{@saveStrategyTask.isRunning}}
              @label={{t "general.save"}}
              @onClick={{this.onSaveClick}}
              data-test-button="Save"
            />
          </div>
        </div>
      {{else}}
        <div class="{{styles.card-header-wrapper}} items-center gap-200">
          <div class="flex flex-col">
            {{#if this.isStrategySeasonal}}
              <div class="flex flex-row items-center gap-200">
                <p
                  class="text-200--regular"
                  data-test-strategy-date-range
                >{{this.dateRangeTitle}}</p>
                {{#if (and this.isOverlappedStrategy (not this.isEditMode))}}
                  <div class={{styles.overlapping-days-badge-wrapper}}>
                    <PrismBadge @iconName="warning" @type="warning">
                      {{t
                        "pricing_assistant.recommendations.seasonal_strategy.days_overlap.label"
                      }}
                      <PrismTooltip
                        @side="bottom"
                        @text={{t
                          "pricing_assistant.recommendations.seasonal_strategy.days_overlap.text"
                        }}
                      />
                    </PrismBadge>
                  </div>
                {{/if}}
              </div>
            {{/if}}
            <h3 data-test-strategy-name>{{this.formattedStrategyTitle}}</h3>
          </div>
          <div class="flex flex-row gap-100">
            {{#unless
              this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
            }}
              <PrismButton
                @buttonType="ghost"
                @icon="edit-pencil"
                @isDisabled={{@isSomeStrategyCardBeingEdited}}
                @onClick={{this.enableEditMode}}
                data-test-button="Enable edit mode"
              />
            {{/unless}}
            {{#if this.showDeleteButton}}
              <PrismButton
                @buttonType="ghost"
                @icon="bin"
                @isDisabled={{@isSomeStrategyCardBeingEdited}}
                @onClick={{this.onDeleteClick}}
                data-test-button="Delete"
              />
            {{/if}}
            <PrismButton
              @buttonType="ghost"
              @icon={{if this.isOpen "chevron-up" "chevron-down"}}
              @onClick={{this.toggleOpen}}
              data-test-button="Toggle open details"
            />
          </div>
        </div>
      {{/if}}
      {{#if this.isOpen}}
        <div class="pr-400 pb-400 pl-400">
          {{#if (and this.isStrategySeasonal this.isEditMode)}}
            <div class="mb-200 flex w-full justify-end">
              <PrismButton
                @buttonType="ghost"
                @iconBefore="undo"
                @isDisabled={{this.isResetToDefaultButtonDisabled}}
                @label={{t
                  "pricing_assistant.recommendations.price_range.reset_to_default"
                }}
                @onClick={{this.onResetToDefaultClick}}
                @size="small"
                data-test-button="Reset to Default"
              />
            </div>
          {{/if}}

          <@strategyComponent
            @componentSeasonEndDt={{this.componentSeasonEndDt}}
            @componentSeasonStartDt={{this.componentSeasonStartDt}}
            @isEditMode={{this.isEditMode}}
            @isSaveStrategyTaskRunning={{@saveStrategyTask.isRunning}}
            @roomOffsetHierarchy={{@roomOffsetHierarchy}}
            @saveAttempted={{this.saveAttempted}}
            @strategy={{@strategy}}
          />

        </div>
      {{/if}}
    </div>
  </template>
}
